import { CheckboxYesNo, FormContainer, LabeledInput, Worksheet } from "uikit"
import HumanTimeSince from "components/HumanTimeSince"
import React from "react"
import { Dropdown, Grid } from "semantic-ui-react"
import { useStore } from "store"
import { AddPersonModal } from "../../../people/AddPersonModal"
import ConnectedAccountBar from "../../ConnectedAccountBar"
import { Footer } from "./Footer"

const RetirementAccountForm = FormContainer(
  {
    balance: {
      type: "unit",
      required: true
    },
    person_id: {
      type: "string"
    },
    basis: {
      type: "unit",
      required: false
    }
  },
  (fields, ...form) => {}
)(
  ({
    data: {
      fields: { person_id, name, balance, basis },
      isModified,
      onSubmit,
      error
    },
    submitLabel = "Done",
    ownerOptions,
    isNew,
    onDelete,
    addNewPerson,
    account
  }) => {
    return (
      <Grid>
        {account.connection_id ? (
          <Grid.Row style={{ borderBottom: "1px solid #ccc" }}>
            <Grid.Column>
              <ConnectedAccountBar {...account} />
            </Grid.Column>
          </Grid.Row>
        ) : null}
        <Grid.Row>
          <Grid.Column>
            <Worksheet error={error} style={{ borderTop: "1px solid #ccc" }}>
              <Worksheet.Body>
                <Worksheet.Row
                  label="What is the current balance of this account?"
                  description={
                    account.connection_id
                      ? "Automatically updated through the account connection"
                      : null
                  }
                  paddedField
                >
                  <Worksheet.Field error={balance.error}>
                    <LabeledInput
                      {...balance}
                      label=""
                      iconText="$"
                      iconPosition="left"
                      placeholder="0"
                      disabled={!!account.connection_id}
                      data-testid="balance"
                    />
                    {!isNew ? (
                      <p
                        style={{
                          fontSize: "0.8em",
                          textAlign: "right",
                          fontWeight: "normal"
                        }}
                      >
                        Updated{" "}
                        <HumanTimeSince time={account.balance_updated_at} /> ago
                      </p>
                    ) : null}
                  </Worksheet.Field>
                </Worksheet.Row>
                <Worksheet.Row
                  label="Who is the account holder/owner?"
                  paddedField
                >
                  <Worksheet.Field error={person_id.error}>
                    <Dropdown
                      {...person_id}
                      placeholder="Account Holder"
                      options={ownerOptions}
                      selection
                      data-testid="accountOwner"
                    />
                    {person_id.value === "__ADD__" && (
                      <AddPersonModal
                        submit={personValues =>
                          addNewPerson(personValues).then(({ personId }) =>
                            person_id.onChange(null, { value: personId })
                          )
                        }
                        onCancel={() =>
                          person_id.onChange(null, { value: null })
                        }
                      />
                    )}
                  </Worksheet.Field>
                </Worksheet.Row>
                <Worksheet.Row
                  label="Have you made after-tax contributions to this account?"
                  paddedField
                  description="This is not common for this type of account."
                >
                  <Worksheet.Field>
                    <CheckboxYesNo
                      fluid
                      checked={basis.value !== ""}
                      toggle={() => {
                        if (basis.value !== "") {
                          basis.onChange(null, { value: "" })
                        } else {
                          basis.onChange(null, { value: 0 })
                        }
                      }}
                      name="basisToggle"
                      data-testid="hasBasis"
                    />
                  </Worksheet.Field>
                  {basis.value !== "" ? (
                    <Worksheet.Row
                      paddedField
                      label="What is your total after-tax contribution to this account since opening?"
                      description="Do not include capital gains on those contributions in this amount."
                    >
                      <Worksheet.Field padded>
                        <LabeledInput
                          {...basis}
                          label=""
                          iconText="$"
                          iconPosition="left"
                          placeholder="0"
                          data-testid="basis"
                        />
                      </Worksheet.Field>
                    </Worksheet.Row>
                  ) : null}
                </Worksheet.Row>
              </Worksheet.Body>
              <Footer
                isNew={isNew}
                submitLabel={submitLabel}
                onSubmit={onSubmit}
                onDelete={onDelete}
                isModified={isModified}
                deleteLabel={
                  account.connection_id ? "Ignore Account" : "Delete Account"
                }
                hasConnectionId={account.connection_id ? true : false}
                error={error}
              />
            </Worksheet>{" "}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    )
  }
)

const RetirementAccount = ({ household, account, ...formProps }) => {
  const isNew = account == null
  const [people, { addPerson }] = useStore("householdPeople", household.id)
  const ownerOptions = Object.values(people)
    .filter(person => person.role === "spouse" || person.role === "client")
    .map(({ id: value, first_name: text }) => ({ value, text }))
  const defaultOwnerId = ownerOptions.length === 1 ? ownerOptions[0].value : ""
  const values = isNew ? { person_id: defaultOwnerId } : account
  const canAddOwner = ownerOptions.length < 2
  if (canAddOwner) {
    ownerOptions.push({ text: "Add person...", value: "__ADD__" })
  }
  const addNewPerson = values =>
    addPerson({ household_id: household.id, ...values })
  return (
    <RetirementAccountForm
      {...formProps}
      ownerOptions={ownerOptions}
      values={values}
      addNewPerson={addNewPerson}
      account={values}
    />
  )
}

export default RetirementAccount
